






















































































import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import CreateHoliday from '@/features/settings/components/CreateHoliday.vue';
import UpdateHoliday from '@/features/settings/components/UpdateHoliday.vue';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import DataTable from '@/shared/components/dataTable/DataTable.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import ClockIcon from 'vue-material-design-icons/ClockOutline.vue';
import { DELETE_HOLIDAY, LOAD_HOLIDAYS } from '@/store/holidays/actionTypes';
import moment from 'moment';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import Toast from '@/shared/services/toast';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import CalendarIcon from 'vue-material-design-icons/CalendarRange.vue';
import BusinessHoursSelection from '@/features/bots/components/businessHoursSelection/BusinessHoursSelection.vue';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { botsApi } from '@/features/bots/services/bots';

@Component({
	components: {
		CalendarIcon,
		ClockIcon,
		ConfirmationModal,
		CreateHoliday,
		DataTable,
		DeleteIcon,
		DropDownMenu,
		LoadingSpinner,
		BusinessHoursSelection,
		TitledSection,
		UpdateHoliday,
	},
})
export default class BusinessHours extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public holidays!: HolidayViewModel[];
	@Getter public getFlow!: (flowId: string) => FlowListItemViewModel | undefined;

	public businessHours: BusinessHoursViewModel[] = [];

	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;
	@Action(LOAD_HOLIDAYS) public loadHolidays: (forceLoad?: boolean) => Promise<HolidayViewModel[]>;
	@Action(DELETE_HOLIDAY) public executeDelete: (holidayId: string) => Promise<undefined>;
	@Action(LOAD_FLOWS) public loadFlows: any;

	public selectedHoliday = {} as HolidayViewModel;

	public createHolidayModalDisplayed: boolean = false;
	public updateHolidayModalDisplayed: boolean = false;
	public showDeleteAttributeModal: boolean = false;
	public isLoading: boolean = true;
	public botAppInstallError: boolean = false;
	public updateInProgress: boolean = false;
	public invalidOpeningTimes: boolean = false;

	public async mounted() {
		try {
			await this.loadHolidays(true);
			this.businessHours = await this.loadBusinessHours();
		} catch (error) {
			if (error.status !== 404) {
				this.handleSeriousError(error.message);
			}
		} finally {
			this.isLoading = false;
		}
	}

	public showCreateHolidayModal() {
		this.createHolidayModalDisplayed = true;
	}

	public closeCreateHolidayModal() {
		this.createHolidayModalDisplayed = false;
	}

	public openEditHoliday(holiday: HolidayViewModel) {
		this.selectedHoliday = holiday;
		this.updateHolidayModalDisplayed = true;
	}

	public closeUpdateHolidayModal() {
		this.updateHolidayModalDisplayed = false;
	}

	public async showDeleteModal(holiday: HolidayViewModel) {
		this.selectedHoliday = holiday;
		if (this.canSelectedHolidayBeDeleted) {
			this.showDeleteAttributeModal = true;
		} else {
			if (this.isHolidayUsedInFlows) {
				await this.loadFlows();
			}
			this.showDeleteAttributeModal = true;
		}
	}

	public get canSelectedHolidayBeDeleted(): boolean {
		return !this.isHolidayUsedInDefaultBusinessHours && !this.isHolidayUsedInFlows;
	}

	public get deleteConfirmationMessage() {
		if (this.canSelectedHolidayBeDeleted) {
			return `Are you sure you want to delete holiday <b>${this.holidayDisplayName}</b>?`;
		} else {
			let warningText = `The holiday <b>${this.holidayDisplayName}</b> cannot be deleted `;
			if (this.isHolidayUsedInDefaultBusinessHours && this.isHolidayUsedInFlows) {
				warningText += `as it's in use in your default business hours and in the following flows: ${this.usedFlowHtmlList}`;
			} else if (this.isHolidayUsedInDefaultBusinessHours) {
				warningText += `as it's in use in your default business hours.`;
			} else if (this.isHolidayUsedInFlows) {
				warningText += `as it's in use in the following flows: ${this.usedFlowHtmlList}`;
			}
			return warningText;
		}
	}

	public async deleteHoliday() {
		this.showDeleteAttributeModal = false;
		if (this.canSelectedHolidayBeDeleted) {
			try {
				await this.executeDelete(this.selectedHoliday.id!);
				Toast.showSuccess(`Successfully deleted holiday: ${this.selectedHoliday.name}`);
			} catch (err) {
				Toast.showError(err.message);
			}
		}
		this.selectedHoliday = {} as HolidayViewModel;
	}

	public getColumns(): DataTableColumn[] {
		return [
			{
				display: 'Name',
				name: 'name',
				clickable: true,
			},
			{
				display: 'When',
				name: 'dates',
				formatter: this.formatWhen,
			},
		];
	}

	public loadBusinessHours() {
		return botsApi.getBusinessHours(this.currentWorkspace.id);
	}

	public openingTimesError($event: any) {
		this.invalidOpeningTimes = $event;
	}

	public save() {
		if (
				this.updateInProgress ||
				this.isLoading ||
				this.botAppInstallError
		) {
			return;
		}
		this.updateInProgress = true;

		const businessHours = this.businessHours.map((val) => {
			if (val.closed) {
				val.startTime = undefined;
				val.endTime = undefined;
			}
			return val;
		});

		botsApi
				.updateBusinessHours(this.currentWorkspace.id, businessHours)
				.then(() => {
					Toast.showSuccess('Successfully updated Bot');
				})
				.catch((err) => {
					Toast.showError(err.message);
				})
				.finally(() => {
					this.updateInProgress = false;
				});
	}

	private formatWhen(dates: Date[]): string {
		if (dates) {
			const datesToDisplay = dates.slice(0, 3);
			const readableDates = datesToDisplay
					.map((date) => moment(date).format('MMMM Do YYYY'))
					.join(', ');
			return dates.length > 3 ? `${readableDates}...` : readableDates;
		}
		return '';
	}

	private get isHolidayUsedInDefaultBusinessHours(): boolean {
		return this.businessHours.some(bh => bh.holidayId === this.selectedHoliday.id);
	}

	private get isHolidayUsedInFlows(): boolean {
		return !!this.selectedHoliday.usedInFlowIds && this.selectedHoliday.usedInFlowIds.length > 0;
	}

	private get usedFlowHtmlList() {
		const flowNames = this.selectedHoliday.usedInFlowIds!.map(flowId => this.getFlowDisplayName(flowId));
		let flowListHtml = '<ul style="list-style: disc outside">';
		flowListHtml += flowNames.map(flowName => `<li>${flowName}</li>`).join('');
		flowListHtml += '</ul>';
		return flowListHtml;
	}

	private getFlowDisplayName(flowId: string): string {
		const flow = this.getFlow(flowId);
		return flow ? HtmlFormatter.escapeHtmlCharacters(flow.name) : flowId;
	}

	private get holidayDisplayName(): string {
		return HtmlFormatter.escapeHtmlCharacters(this.selectedHoliday.name);
	}
}
